import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Target, Shield, Lock, Users, BarChart3, FileText, UserPlus, Download,
  Trash2, Search, LogOut, Code, Globe, Pencil, Save, AlertTriangle, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token') || '');
  const [admin, setAdmin] = useState(null);
  const [loginStep, setLoginStep] = useState('email');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [loading, setLoading] = useState(false);

  // Dashboard state
  const [activeTab, setActiveTab] = useState('leads');
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsTotal, setAssessmentsTotal] = useState(0);
  const [team, setTeam] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Settings state
  const [siteSettings, setSiteSettings] = useState(null);
  const [settingsSection, setSettingsSection] = useState('code');
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Team form
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [showAddMember, setShowAddMember] = useState(false);

  const adminApi = useCallback(() => {
    return axios.create({ headers: { Authorization: `Bearer ${adminToken}` } });
  }, [adminToken]);

  useEffect(() => { if (adminToken) verifyAdmin(); }, [adminToken]);

  const verifyAdmin = async () => {
    try {
      const res = await adminApi().get(`${API}/admin/me`);
      setAdmin(res.data);
      fetchStats();
      fetchLeads();
    } catch {
      setAdminToken('');
      localStorage.removeItem('admin_token');
    }
  };

  const fetchStats = async () => { try { const r = await adminApi().get(`${API}/admin/stats`); setStats(r.data); } catch {} };
  const fetchLeads = async () => { try { const r = await adminApi().get(`${API}/admin/leads?limit=100`); setLeads(r.data.leads); setLeadsTotal(r.data.total); } catch {} };
  const fetchUsers = async () => { try { const r = await adminApi().get(`${API}/admin/users?limit=100`); setUsers(r.data.users); setUsersTotal(r.data.total); } catch {} };
  const fetchAssessments = async () => { try { const r = await adminApi().get(`${API}/admin/assessments?limit=100`); setAssessments(r.data.assessments); setAssessmentsTotal(r.data.total); } catch {} };
  const fetchTeam = async () => { try { const r = await adminApi().get(`${API}/admin/team`); setTeam(r.data.team); } catch {} };
  const fetchSettings = async () => { try { const r = await adminApi().get(`${API}/admin/site-settings`); setSiteSettings(r.data); } catch {} };

  useEffect(() => {
    if (!admin) return;
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'assessments') fetchAssessments();
    if (activeTab === 'team') fetchTeam();
    if (activeTab === 'settings') fetchSettings();
  }, [activeTab, admin]);

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email: loginEmail, password: loginPassword });
      setDevOtp(res.data._dev_otp || '');
      setLoginStep('otp');
      if (res.data._dev_otp) {
        toast.success(`Your OTP is: ${res.data._dev_otp}`, { duration: 30000 });
      } else {
        toast.success('OTP sent! Check your email.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/verify-otp`, { email: loginEmail, otp });
      setAdminToken(res.data.token);
      localStorage.setItem('admin_token', res.data.token);
      setAdmin(res.data.admin);
      toast.success(`Welcome, ${res.data.admin.name}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    setAdminToken(''); setAdmin(null);
    localStorage.removeItem('admin_token');
    setLoginStep('email'); setLoginEmail(''); setLoginPassword(''); setOtp('');
  };

  const updateLeadStatus = async (id, status) => {
    try { await adminApi().put(`${API}/admin/leads/${id}/status`, { status }); toast.success('Status updated'); fetchLeads(); } catch { toast.error('Failed to update'); }
  };

  const exportCSV = () => { window.open(`${API}/admin/leads/export`, '_blank'); };

  const addTeamMember = async (e) => {
    e.preventDefault();
    try {
      await adminApi().post(`${API}/admin/team`, newMember);
      toast.success('Team member added');
      setNewMember({ name: '', email: '', password: '', role: 'admin' });
      setShowAddMember(false);
      fetchTeam();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
  };

  const removeMember = async (id, name) => {
    if (!window.confirm(`Remove ${name} from team?`)) return;
    try { await adminApi().delete(`${API}/admin/team/${id}`); toast.success('Member removed'); fetchTeam(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  const saveSettings = async () => {
    if (!siteSettings) return;
    setSettingsSaving(true);
    try {
      const res = await adminApi().put(`${API}/admin/site-settings`, {
        code_injection: siteSettings.code_injection,
        seo: siteSettings.seo,
        content: siteSettings.content
      });
      setSiteSettings(res.data.settings);
      toast.success('Settings saved! Changes will appear on next page load.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally { setSettingsSaving(false); }
  };

  const updateSettingsField = (section, field, value) => {
    setSiteSettings(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const STATUS_COLORS = {
    new: 'bg-blue-100 text-blue-700',
    contacted: 'bg-yellow-100 text-yellow-700',
    converted: 'bg-green-100 text-green-700',
    closed: 'bg-gray-100 text-gray-700'
  };

  // ===== LOGIN SCREEN =====
  if (!admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-earth-700">Admin Panel</h1>
            <p className="text-earth-500 mt-1">O4S.ai Dashboard</p>
          </div>
          <div className="bg-white rounded-2xl border border-earth-200 shadow-sm p-6">
            {loginStep === 'email' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <h3 className="text-lg font-bold text-earth-700 mb-1">Sign In</h3>
                <p className="text-sm text-earth-500 mb-4">Enter your admin credentials</p>
                <div>
                  <label className="text-sm font-semibold text-earth-600 mb-1 block">Email</label>
                  <Input data-testid="admin-email" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="admin@o4s.ai" className="border-earth-200 rounded-xl" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-earth-600 mb-1 block">Password</label>
                  <Input data-testid="admin-password" type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="Enter password" className="border-earth-200 rounded-xl" />
                </div>
                <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full bg-secondary hover:bg-secondary-hover text-white h-12 rounded-xl btn-press">
                  {loading ? 'Verifying...' : 'Continue'} <Lock className="ml-2 w-4 h-4" />
                </Button>
              </form>
            ) : (
              <form onSubmit={handleOTP} className="space-y-4">
                <h3 className="text-lg font-bold text-earth-700 mb-1">2FA Verification</h3>
                <p className="text-sm text-earth-500 mb-2">Enter the 6-digit OTP sent to {loginEmail}</p>
                {devOtp && (
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-5 text-center">
                    <p className="text-sm text-amber-700 font-semibold mb-2">Your OTP Code</p>
                    <p data-testid="admin-dev-otp" className="text-4xl font-bold text-amber-800 tracking-[0.4em]">{devOtp}</p>
                    <p className="text-xs text-amber-600 mt-2">Enter this code below to complete login</p>
                  </div>
                )}
                <Input data-testid="admin-otp" type="text" value={otp} onChange={e => setOtp(e.target.value)} placeholder="Enter 6-digit OTP" maxLength={6}
                  className="border-earth-200 rounded-xl text-center text-2xl tracking-[0.3em] h-14" />
                <Button data-testid="admin-verify-btn" type="submit" disabled={loading} className="w-full bg-secondary hover:bg-secondary-hover text-white h-12 rounded-xl btn-press">
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </Button>
                <button type="button" onClick={() => setLoginStep('email')} className="w-full text-sm text-earth-500 hover:text-primary">Back to login</button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== DASHBOARD =====
  const filteredLeads = searchQuery ? leads.filter(l =>
    l.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.phone?.includes(searchQuery) ||
    l.city?.toLowerCase().includes(searchQuery.toLowerCase())
  ) : leads;

  const TABS = [
    { id: 'leads', label: 'Leads', icon: FileText, count: stats?.leads?.total },
    { id: 'users', label: 'Users', icon: Users, count: stats?.users?.total },
    { id: 'assessments', label: 'Assessments', icon: BarChart3, count: stats?.assessments?.total },
    ...(admin.role === 'super_admin' ? [
      { id: 'team', label: 'Team', icon: Shield, count: team.length },
      { id: 'settings', label: 'Settings', icon: Code }
    ] : [])
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Nav */}
      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 py-3 border-b border-earth-200">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-secondary rounded-xl flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-earth-700">O4S.ai Admin</h1>
              <p className="text-xs text-earth-500">{admin.name} &middot; <span className="capitalize">{admin.role.replace('_', ' ')}</span></p>
            </div>
          </div>
          <Button data-testid="admin-logout" onClick={handleLogout} variant="ghost" size="sm" className="text-earth-500 hover:text-red-600">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-earth-200 p-5">
              <p className="text-xs uppercase tracking-wider text-earth-500 font-semibold">Total Leads</p>
              <p className="text-3xl font-bold text-primary mt-1">{stats.leads.total}</p>
              <p className="text-xs text-earth-400 mt-1">{stats.leads.new} new &middot; {stats.leads.recent_7d} this week</p>
            </div>
            <div className="bg-white rounded-xl border border-earth-200 p-5">
              <p className="text-xs uppercase tracking-wider text-earth-500 font-semibold">Total Users</p>
              <p className="text-3xl font-bold text-secondary mt-1">{stats.users.total}</p>
              <p className="text-xs text-earth-400 mt-1">{stats.users.google} Google &middot; {stats.users.email} Email</p>
            </div>
            <div className="bg-white rounded-xl border border-earth-200 p-5">
              <p className="text-xs uppercase tracking-wider text-earth-500 font-semibold">Assessments</p>
              <p className="text-3xl font-bold text-accent mt-1">{stats.assessments.total}</p>
              <p className="text-xs text-earth-400 mt-1">AI career analyses</p>
            </div>
            <div className="bg-white rounded-xl border border-earth-200 p-5">
              <p className="text-xs uppercase tracking-wider text-earth-500 font-semibold">Recent Users (7d)</p>
              <p className="text-3xl font-bold text-warning mt-1">{stats.users.recent_7d}</p>
              <p className="text-xs text-earth-400 mt-1">New signups this week</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl border border-earth-200 p-1 overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab.id} data-testid={`admin-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-secondary text-white' : 'text-earth-500 hover:text-earth-700 hover:bg-earth-50'}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count != null && <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20' : 'bg-earth-100'}`}>{tab.count}</span>}
            </button>
          ))}
        </div>

        {/* LEADS TAB */}
        {activeTab === 'leads' && (
          <div className="animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-earth-400 w-4 h-4" />
                <Input data-testid="admin-search-leads" placeholder="Search by name, email, phone, city..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10 border-earth-200 rounded-xl" />
              </div>
              <Button data-testid="admin-export-csv" onClick={exportCSV} variant="outline" className="border-earth-200 text-earth-600 rounded-xl btn-press">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
            <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-earth-50 border-b border-earth-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Contact</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">City</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Education</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Future Plan</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Source</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead, idx) => (
                      <tr key={lead.id} data-testid={`lead-row-${idx}`} className="border-b border-earth-100 hover:bg-earth-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-earth-700 text-sm">{lead.name}</div>
                          {lead.dream_career && <div className="text-xs text-earth-400">Wants: {lead.dream_career}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-earth-600">{lead.email}</div>
                          <div className="text-xs text-earth-400">{lead.phone}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-earth-600">{lead.city}</td>
                        <td className="px-4 py-3 text-sm text-earth-600">{lead.education_level}</td>
                        <td className="px-4 py-3 text-sm text-earth-600">{lead.future_plan}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${lead.source === 'results_gate' ? 'bg-purple-100 text-purple-700' : lead.source ? 'bg-blue-100 text-blue-700' : 'bg-earth-100 text-earth-500'}`}>
                            {lead.source || 'direct'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <select data-testid={`lead-status-${idx}`} value={lead.status} onChange={e => updateLeadStatus(lead.id, e.target.value)}
                            className={`text-xs font-semibold px-2 py-1 rounded-lg border-0 cursor-pointer ${STATUS_COLORS[lead.status] || 'bg-gray-100'}`}>
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="converted">Converted</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-xs text-earth-400">{new Date(lead.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                    {filteredLeads.length === 0 && (
                      <tr><td colSpan={8} className="px-4 py-12 text-center text-earth-400">No leads found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <div className="animate-fade-in bg-white rounded-xl border border-earth-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-earth-50 border-b border-earth-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Provider</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr key={u.id} data-testid={`user-row-${idx}`} className="border-b border-earth-100 hover:bg-earth-50/50">
                      <td className="px-4 py-3 font-semibold text-earth-700 text-sm">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-earth-600">{u.email}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-lg ${u.auth_provider === 'google' ? 'bg-blue-100 text-blue-700' : 'bg-earth-100 text-earth-600'}`}>{u.auth_provider || 'email'}</span></td>
                      <td className="px-4 py-3 text-xs text-earth-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-earth-400">No users found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ASSESSMENTS TAB */}
        {activeTab === 'assessments' && (
          <div className="animate-fade-in bg-white rounded-xl border border-earth-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-earth-50 border-b border-earth-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">User ID</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Education</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Stream</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Career Paths</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map((a, idx) => (
                    <tr key={a.id} data-testid={`assessment-row-${idx}`} className="border-b border-earth-100 hover:bg-earth-50/50">
                      <td className="px-4 py-3 text-xs font-mono text-earth-500">{a.user_id?.substring(0, 8)}...</td>
                      <td className="px-4 py-3 text-sm text-earth-600">{a.data?.education_level}</td>
                      <td className="px-4 py-3 text-sm text-earth-600">{a.data?.board_stream}</td>
                      <td className="px-4 py-3 text-sm text-earth-600">{a.career_paths?.length || 0} paths</td>
                      <td className="px-4 py-3 text-xs text-earth-400">{a.created_at ? new Date(a.created_at).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  ))}
                  {assessments.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-earth-400">No assessments found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TEAM TAB */}
        {activeTab === 'team' && admin.role === 'super_admin' && (
          <div className="animate-fade-in space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-earth-700">Team Members</h3>
              <Button data-testid="admin-add-member" onClick={() => setShowAddMember(!showAddMember)} className="bg-secondary hover:bg-secondary-hover text-white rounded-xl btn-press" size="sm">
                <UserPlus className="w-4 h-4 mr-2" /> Add Member
              </Button>
            </div>
            {showAddMember && (
              <form onSubmit={addTeamMember} className="bg-white rounded-xl border border-earth-200 p-5 space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <Input placeholder="Name" value={newMember.name} onChange={e => setNewMember({...newMember, name: e.target.value})} className="border-earth-200 rounded-xl" />
                  <Input placeholder="Email" type="email" value={newMember.email} onChange={e => setNewMember({...newMember, email: e.target.value})} className="border-earth-200 rounded-xl" />
                  <Input placeholder="Password" type="password" value={newMember.password} onChange={e => setNewMember({...newMember, password: e.target.value})} className="border-earth-200 rounded-xl" />
                  <select value={newMember.role} onChange={e => setNewMember({...newMember, role: e.target.value})}
                    className="border border-earth-200 rounded-xl px-3 py-2 text-sm">
                    <option value="admin">Admin (View Only)</option>
                    <option value="super_admin">Super Admin (Full Access)</option>
                  </select>
                </div>
                <Button type="submit" className="bg-primary hover:bg-primary-hover text-white rounded-xl btn-press" size="sm">Add Member</Button>
              </form>
            )}
            <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-earth-50 border-b border-earth-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Role</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-earth-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {team.map((m, idx) => (
                    <tr key={m.id} data-testid={`team-row-${idx}`} className="border-b border-earth-100">
                      <td className="px-4 py-3 font-semibold text-earth-700 text-sm">{m.name}</td>
                      <td className="px-4 py-3 text-sm text-earth-600">{m.email}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-lg ${m.role === 'super_admin' ? 'bg-red-100 text-red-700' : 'bg-earth-100 text-earth-600'}`}>{m.role.replace('_', ' ')}</span></td>
                      <td className="px-4 py-3">
                        {m.email !== 'admin@o4s.ai' && (
                          <button onClick={() => removeMember(m.id, m.name)} className="text-red-500 hover:text-red-700 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SETTINGS TAB (Super Admin) */}
        {activeTab === 'settings' && admin.role === 'super_admin' && siteSettings && (
          <div className="animate-fade-in space-y-6">
            {/* Settings Sub-Tabs */}
            <div className="flex gap-2">
              {[
                { id: 'code', label: 'Code Injection', icon: Code },
                { id: 'seo', label: 'SEO Meta Tags', icon: Globe },
                { id: 'content', label: 'Content Editor', icon: Pencil }
              ].map(s => (
                <button key={s.id} data-testid={`settings-tab-${s.id}`}
                  onClick={() => setSettingsSection(s.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${settingsSection === s.id ? 'bg-primary text-white border-primary' : 'bg-white text-earth-500 border-earth-200 hover:border-primary hover:text-primary'}`}>
                  <s.icon className="w-4 h-4" /> {s.label}
                </button>
              ))}
            </div>

            {/* CODE INJECTION */}
            {settingsSection === 'code' && (
              <div data-testid="settings-code-injection" className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-800">Paste GTM, Analytics, or any custom scripts here</p>
                    <p className="text-xs text-yellow-600 mt-1">Code is injected into the live site. Include full {'<script>'} tags. Changes apply on next page load.</p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-earth-200 p-6 space-y-5">
                  <div>
                    <label className="text-sm font-bold text-earth-700 mb-2 flex items-center gap-2">
                      <Code className="w-4 h-4 text-primary" /> Header Code <span className="text-xs font-normal text-earth-400">{'(injected into <head>)'}</span>
                    </label>
                    <Textarea data-testid="code-header"
                      value={siteSettings.code_injection?.header_code || ''}
                      onChange={e => updateSettingsField('code_injection', 'header_code', e.target.value)}
                      placeholder={'<!-- GTM Head snippet -->\n<script>(function(w,d,s,l,i){...})(window,document,\'script\',\'dataLayer\',\'GTM-XXXX\');</script>'}
                      className="font-mono text-sm border-earth-200 rounded-xl min-h-[140px]" />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-earth-700 mb-2 flex items-center gap-2">
                      <Code className="w-4 h-4 text-secondary" /> Body Code <span className="text-xs font-normal text-earth-400">{'(injected after <body>)'}</span>
                    </label>
                    <Textarea data-testid="code-body"
                      value={siteSettings.code_injection?.body_code || ''}
                      onChange={e => updateSettingsField('code_injection', 'body_code', e.target.value)}
                      placeholder={'<!-- GTM noscript / body snippet -->\n<noscript><iframe src="..."></iframe></noscript>'}
                      className="font-mono text-sm border-earth-200 rounded-xl min-h-[140px]" />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-earth-700 mb-2 flex items-center gap-2">
                      <Code className="w-4 h-4 text-accent" /> Footer Code <span className="text-xs font-normal text-earth-400">{'(injected before </body>)'}</span>
                    </label>
                    <Textarea data-testid="code-footer"
                      value={siteSettings.code_injection?.footer_code || ''}
                      onChange={e => updateSettingsField('code_injection', 'footer_code', e.target.value)}
                      placeholder={'<!-- Chat widget, conversion pixels, etc. -->\n<script src="https://..."></script>'}
                      className="font-mono text-sm border-earth-200 rounded-xl min-h-[140px]" />
                  </div>
                </div>
              </div>
            )}

            {/* SEO META TAGS */}
            {settingsSection === 'seo' && (
              <div data-testid="settings-seo" className="bg-white rounded-xl border border-earth-200 p-6 space-y-5">
                <h3 className="text-lg font-bold text-earth-700 flex items-center gap-2"><Globe className="w-5 h-5 text-primary" /> SEO & Open Graph Settings</h3>
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">Site Title</label>
                    <Input data-testid="seo-title"
                      value={siteSettings.seo?.site_title || ''}
                      onChange={e => updateSettingsField('seo', 'site_title', e.target.value)}
                      placeholder="O4S.ai — Career Guidance for Indian Students"
                      className="border-earth-200 rounded-xl" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">Site Description</label>
                    <Textarea data-testid="seo-description"
                      value={siteSettings.seo?.site_description || ''}
                      onChange={e => updateSettingsField('seo', 'site_description', e.target.value)}
                      placeholder="AI-powered career path guidance..."
                      className="border-earth-200 rounded-xl min-h-[80px]" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">Keywords <span className="text-xs font-normal text-earth-400">(comma-separated)</span></label>
                    <Input data-testid="seo-keywords"
                      value={siteSettings.seo?.keywords || ''}
                      onChange={e => updateSettingsField('seo', 'keywords', e.target.value)}
                      placeholder="career guidance, Indian students, AP, Telangana"
                      className="border-earth-200 rounded-xl" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">OG Title</label>
                    <Input data-testid="seo-og-title"
                      value={siteSettings.seo?.og_title || ''}
                      onChange={e => updateSettingsField('seo', 'og_title', e.target.value)}
                      placeholder="O4S.ai — Your Career Starts Here"
                      className="border-earth-200 rounded-xl" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">OG Image URL</label>
                    <Input data-testid="seo-og-image"
                      value={siteSettings.seo?.og_image || ''}
                      onChange={e => updateSettingsField('seo', 'og_image', e.target.value)}
                      placeholder="https://example.com/og-image.png"
                      className="border-earth-200 rounded-xl" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">OG Description</label>
                    <Textarea data-testid="seo-og-desc"
                      value={siteSettings.seo?.og_description || ''}
                      onChange={e => updateSettingsField('seo', 'og_description', e.target.value)}
                      placeholder="Discover your perfect career path..."
                      className="border-earth-200 rounded-xl min-h-[70px]" />
                  </div>
                </div>
              </div>
            )}

            {/* CONTENT EDITOR */}
            {settingsSection === 'content' && (
              <div data-testid="settings-content" className="bg-white rounded-xl border border-earth-200 p-6 space-y-5">
                <h3 className="text-lg font-bold text-earth-700 flex items-center gap-2"><Pencil className="w-5 h-5 text-primary" /> Landing Page Content</h3>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">Hero Title</label>
                    <Input data-testid="content-hero-title"
                      value={siteSettings.content?.hero_title || ''}
                      onChange={e => updateSettingsField('content', 'hero_title', e.target.value)}
                      placeholder="Your Future Starts Here"
                      className="border-earth-200 rounded-xl" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">CTA Button Text</label>
                    <Input data-testid="content-cta-text"
                      value={siteSettings.content?.cta_text || ''}
                      onChange={e => updateSettingsField('content', 'cta_text', e.target.value)}
                      placeholder="Start Free Assessment"
                      className="border-earth-200 rounded-xl" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">Hero Subtitle</label>
                    <Textarea data-testid="content-hero-subtitle"
                      value={siteSettings.content?.hero_subtitle || ''}
                      onChange={e => updateSettingsField('content', 'hero_subtitle', e.target.value)}
                      placeholder="AI-powered career guidance tailored for Indian students"
                      className="border-earth-200 rounded-xl min-h-[70px]" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-earth-600 mb-1 block">About Text</label>
                    <Textarea data-testid="content-about"
                      value={siteSettings.content?.about_text || ''}
                      onChange={e => updateSettingsField('content', 'about_text', e.target.value)}
                      placeholder="O4S.ai helps students in AP and Telangana..."
                      className="border-earth-200 rounded-xl min-h-[100px]" />
                  </div>
                </div>
              </div>
            )}

            {/* Save Button — always visible in Settings */}
            <div className="flex justify-end">
              <Button data-testid="settings-save-btn" onClick={saveSettings} disabled={settingsSaving}
                className="bg-primary hover:bg-primary-hover text-white h-12 px-8 rounded-xl btn-press gap-2">
                {settingsSaving ? 'Saving...' : <><Save className="w-4 h-4" /> Save All Settings</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
